import React, { useState, useEffect } from 'react';
import { getVetoStatsForTeamBySeason } from '../../services/MatchService';
import Loading from '../../components/Loading';
import { MapStats } from '../../models/MapStats';
import useLocalStorage from 'use-local-storage';
import inferno from "../../content/maps/de_inferno.jpg";
import mirage from "../../content/maps/de_mirage.jpg";
import nuke from "../../content/maps/de_nuke.jpg";
import overpass from "../../content/maps/de_overpass.jpg";
import ancient from "../../content/maps/de_ancient.jpg";
import vertigo from "../../content/maps/de_vertigo.jpg";
import anubis from "../../content/maps/de_anubis.jpg";
import dust2 from "../../content/maps/de_dust2.jpg";
import train from "../../content/maps/de_train.jpg";
import { currentSeason } from '../../Constants';

// Interface for component props
interface TeamVetoComparisonProps {
  team1Id: string;
  team2Id: string;
  team1Name: string;
  team2Name: string;
}

// Map image mappings
const mapImageMappings: Record<string, string> = {
  "Inferno": inferno,
  "Mirage": mirage,
  "Nuke": nuke,
  "Overpass": overpass,
  "Vertigo": vertigo,
  "Ancient": ancient,
  "Anubis": anubis,
  "Dust2": dust2,
  "Train": train
};

// Color palette for light and dark modes
const colorPalette = {
  light: {
    pickBg: '#3498db',         // Brighter blue for picks in light mode
    pickText: 'white',
    banBg: '#e74c3c',          // Brighter red for bans in light mode
    banText: 'white',
    neutralBg: '#f1f1f1',
    neutralText: '#333',
    highWinRate: '#2ecc71',    // Green for high win rates
    lowWinRate: '#e74c3c',     // Red for low win rates
    neutralWinRate: 'inherit'
  },
  dark: {
    pickBg: 'rgba(52, 152, 219, 0.7)',  // Darker blue with transparency
    pickText: 'white',
    banBg: 'rgba(231, 76, 60, 0.7)',    // Darker red with transparency
    banText: 'white',
    neutralBg: '#333',
    neutralText: '#fff',
    highWinRate: '#27ae60',    // Darker green for high win rates
    lowWinRate: '#c0392b',     // Darker red for low win rates
    neutralWinRate: 'inherit'
  }
};

const TeamVetoComparison: React.FC<TeamVetoComparisonProps> = ({ team1Id, team2Id, team1Name, team2Name }) => {
  const [team1Stats, setTeam1Stats] = useState<MapStats[]>([]);
  const [team2Stats, setTeam2Stats] = useState<MapStats[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDark] = useLocalStorage<boolean>('isDark', false);

  // Get current color theme
  const colors = isDark ? colorPalette.dark : colorPalette.light;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [stats1, stats2] = await Promise.all([
          getVetoStatsForTeamBySeason(team1Id, currentSeason.toString()),
          getVetoStatsForTeamBySeason(team2Id, currentSeason.toString())
        ]);
        
        setTeam1Stats(stats1);
        setTeam2Stats(stats2);
      } catch (error) {
        console.error('Error fetching team veto stats:', error);
      } finally {
        setLoading(false);
      }
    };

    if (team1Id && team2Id) {
      fetchData();
    }
  }, [team1Id, team2Id]);

  // Calculate win percentage
  const getWinPercentage = (stats: MapStats | undefined): number => {
    if (!stats || stats.amountPlayed === 0) return 0;
    return Math.round((stats.amountWon / stats.amountPlayed) * 100);
  };

  if (loading) {
    return <Loading />;
  }

  // Get all unique map names from both teams
  const allMaps: string[] = Array.from(new Set([
    ...team1Stats.map(stat => stat.mapName),
    ...team2Stats.map(stat => stat.mapName)
  ]));

  // Function to determine win rate color
  const getWinRateColor = (rate: number): string => {
    if (rate === 0) return colors.lowWinRate; // Red for 0%
    if (rate >= 75) return colors.highWinRate; // Green for high win rates
    return colors.neutralWinRate;
  };

  // Create a reusable badge component
  const Badge = ({ type, text }: { type: 'played' | 'pick' | 'ban', text: string }) => {
    let bgColor: string;
    let textColor: string;
    
    switch (type) {
      case 'pick':
        bgColor = colors.pickBg;
        textColor = colors.pickText;
        break;
      case 'ban':
        bgColor = colors.banBg;
        textColor = colors.banText;
        break;
      default:
        bgColor = colors.neutralBg;
        textColor = colors.neutralText;
    }
    
    return (
      <span style={{
        backgroundColor: bgColor,
        padding: '2px 5px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 'bold',
        color: textColor,
        border: type === 'played' ? (isDark ? '1px solid #444' : '1px solid #ddd') : 'none',
        whiteSpace: 'nowrap'
      }}>
        {text}
      </span>
    );
  };

  return (
    <div className={`map-comparison ${isDark ? 'bg-dark text-light' : ''}`} 
         style={{ 
           width: '100%', 
           height: '100%',
           backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
           borderRadius: '8px',
           padding: '10px',
           margin: '0',
           boxShadow: isDark ? '0 4px 8px rgba(0, 0, 0, 0.3)' : '0 4px 8px rgba(0, 0, 0, 0.1)'
         }}>
      
      <div style={{ 
        borderRadius: '4px', 
        overflow: 'hidden',
        border: isDark ? '1px solid #333' : '1px solid #ddd'
      }}>
        {allMaps.map((mapName, index) => {
          const team1MapStats = team1Stats.find(stat => stat.mapName === mapName);
          const team2MapStats = team2Stats.find(stat => stat.mapName === mapName);
          
          const team1WinRate = getWinPercentage(team1MapStats);
          const team2WinRate = getWinPercentage(team2MapStats);
          
          return (
            <div key={mapName} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              backgroundColor: index % 2 === 0 ? (isDark ? '#212121' : '#f8f8f8') : (isDark ? '#2c2c2c' : '#ffffff'),
              borderBottom: index !== allMaps.length - 1 ? (isDark ? '1px solid #333' : '1px solid #ddd') : 'none'
            }}>
              {/* Team 1 Stats */}
              <div style={{ 
                padding: '10px 10px', 
                textAlign: 'center', 
                width: '200px' 
              }}>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold',
                  color: getWinRateColor(team1WinRate),
                  textAlign: 'center',
                  marginBottom: '6px'
                }}>
                  {team1WinRate}%
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexWrap: 'nowrap',
                  justifyContent: 'center'
                }}>
                  <Badge 
                    type="played"
                    text={`${team1MapStats?.amountPlayed || 0} maps`}
                  />
                  <Badge 
                    type="pick" 
                    text={`${team1MapStats?.amountPicked || 0} picks`}
                  />
                  <Badge 
                    type="ban"
                    text={`${team1MapStats?.amountBanned || 0} bans`}
                  />
                </div>
              </div>
              
              {/* Map in center */}
              <div style={{
                flex: '1',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px 0'
              }}>
                <div style={{
                  position: 'relative',
                  width: '220px',
                  height: '60px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  <img 
                    src={mapImageMappings[mapName]} 
                    alt={mapName}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                    fontSize: '20px'
                  }}>
                    {mapName}
                  </div>
                </div>
              </div>
              
              {/* Team 2 Stats */}
              <div style={{ 
                padding: '10px 10px', 
                textAlign: 'center',
                width: '200px'
              }}>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold',
                  color: getWinRateColor(team2WinRate),
                  textAlign: 'center',
                  marginBottom: '6px'
                }}>
                  {team2WinRate}%
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  flexWrap: 'nowrap',
                  justifyContent: 'center'
                }}>
                  <Badge 
                    type="ban"
                    text={`${team2MapStats?.amountBanned || 0} bans`}
                  />
                  <Badge 
                    type="pick"
                    text={`${team2MapStats?.amountPicked || 0} picks`}
                  />
                  <Badge 
                    type="played"
                    text={`${team2MapStats?.amountPlayed || 0} maps`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamVetoComparison;